import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";
import { LogoOnly } from "../../assets/icons/logoOnly";

// Bespoke "bookmarks for teachers" page. Companion to /bookmarks-for-schools and
// /bookmarks-for-students, targeting the teacher/educator slice of the edu
// query cluster. Localized via the `teachersPage.*` translation keys so it's
// indexable in every site language (not in NON_LOCALIZED_ROUTES).

const BookmarksForTeachers = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const faqData = [
    { q: t("teachersPage.faq.best.q"), a: t("teachersPage.faq.best.a") },
    { q: t("teachersPage.faq.separate.q"), a: t("teachersPage.faq.separate.a") },
    { q: t("teachersPage.faq.free.q"), a: t("teachersPage.faq.free.a") },
    { q: t("teachersPage.faq.schoolComputers.q"), a: t("teachersPage.faq.schoolComputers.a") },
    { q: t("teachersPage.faq.reuse.q"), a: t("teachersPage.faq.reuse.a") },
    { q: t("teachersPage.faq.private.q"), a: t("teachersPage.faq.private.a") },
  ];

  useEffect(() => {
    if (location.hash === "#how-it-works") {
      const element = document.getElementById("how-it-works");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location.hash]);

  return (
    <>
      <SeoTags
        title={t("teachersPage.seo.title")}
        description={t("teachersPage.seo.description")}
        keywords={t("teachersPage.seo.keywords")}
      />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("teachersPage.seo.schemaName"),
            description: t("teachersPage.seo.description"),
            url: "https://onbookmarks.com/bookmarks-for-teachers",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "EducationApplication",
              operatingSystem: "Web, iOS, Android",
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
              },
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          }),
        }}
      />

      {/* Hero */}
      <section className="hero bg-light">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-8 mx-auto text-center">
              <div>
                <div className="d-inline-block rounded-4 bg-warning-subtle fs-normal text-warning px-3 py-2 mb-4">
                  <i className="bi bi-easel me-2"></i>
                  {t("teachersPage.hero.badge")}
                </div>
              </div>
              <h1 className="display-4 fw-bold mb-4">{t("teachersPage.hero.title")}</h1>
              <p className="lead mb-4">{t("teachersPage.hero.lead")}</p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-primary btn-lg px-5"
                >
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  {t("teachersPage.hero.ctaPrimary")}
                </Link>
                <Link to="#how-it-works" className="btn btn-outline-primary btn-lg px-4">
                  {t("teachersPage.hero.ctaSecondary")}
                </Link>
              </div>
              <p className="mt-3 text-muted">
                <i className="bi bi-shield-check me-2"></i>
                {t("teachersPage.hero.guarantee")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-4 bg-white border-bottom">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12">
              <div className="d-flex justify-content-center align-items-center gap-5 flex-wrap">
                <div className="text-center">
                  <i className="bi bi-collection h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">{t("teachersPage.trust.spaces")}</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-robot h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">{t("teachersPage.trust.organized")}</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-arrow-repeat h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">{t("teachersPage.trust.reuse")}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">{t("teachersPage.problem.title")}</h2>
            <p className="lead text-muted">{t("teachersPage.problem.subtitle")}</p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-danger-subtle rounded-3 mb-3">
                    <i className="bi bi-folder2-open fs-3 text-danger"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("teachersPage.problem.scattered.title")}</h3>
                  <p className="text-muted mb-0">{t("teachersPage.problem.scattered.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3">
                    <i className="bi bi-arrow-counterclockwise fs-3 text-warning"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("teachersPage.problem.rebuilt.title")}</h3>
                  <p className="text-muted mb-0">{t("teachersPage.problem.rebuilt.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-secondary-subtle rounded-3 mb-3">
                    <i className="bi bi-layers fs-3 text-secondary"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("teachersPage.problem.blur.title")}</h3>
                  <p className="text-muted mb-0">{t("teachersPage.problem.blur.body")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="text-center mb-5">
                <h2 className="display-6 fw-bold mb-4">{t("teachersPage.guide.title")}</h2>
                <p className="lead text-muted">{t("teachersPage.guide.subtitle")}</p>
              </div>

              <div className="card shadow-sm border-0">
                <div className="card-body p-5">
                  <div className="row g-4">
                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">1</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">{t("teachersPage.guide.step1.title")}</h4>
                          <p className="text-muted mb-0">{t("teachersPage.guide.step1.body")}</p>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">2</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">{t("teachersPage.guide.step2.title")}</h4>
                          <p className="text-muted mb-0">{t("teachersPage.guide.step2.body")}</p>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">3</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">{t("teachersPage.guide.step3.title")}</h4>
                          <p className="text-muted mb-3">{t("teachersPage.guide.step3.body")}</p>
                          <Link
                            to={`${process.env.REACT_APP_APP_URL}/register`}
                            className="btn btn-primary"
                          >
                            {t("teachersPage.guide.step3.button")}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why teachers pick it */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">{t("teachersPage.why.title")}</h2>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-primary-subtle rounded-3 mb-3">
                    <i className="bi bi-robot fs-3 text-primary"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("teachersPage.why.organizes.title")}</h3>
                  <p className="text-muted mb-0">{t("teachersPage.why.organizes.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-success-subtle rounded-3 mb-3">
                    <i className="bi bi-cash-coin fs-3 text-success"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("teachersPage.why.free.title")}</h3>
                  <p className="text-muted mb-0">{t("teachersPage.why.free.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3">
                    <i className="bi bi-collection fs-3 text-warning"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("teachersPage.why.spaces.title")}</h3>
                  <p className="text-muted mb-0">{t("teachersPage.why.spaces.body")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="h3 fw-bold mb-3">{t("teachersPage.more.title")}</h2>
          </div>
          <div className="row g-4 justify-content-center">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm text-center">
                <div className="card-body p-4">
                  <i className="bi bi-building fs-1 text-info mb-3 d-block"></i>
                  <h3 className="h5 mb-2">{t("teachersPage.more.schools.title")}</h3>
                  <p className="text-muted mb-3">{t("teachersPage.more.schools.body")}</p>
                  <Link to="/bookmarks-for-schools" className="btn btn-sm btn-outline-primary">
                    {t("teachersPage.more.schools.link")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm text-center">
                <div className="card-body p-4">
                  <i className="bi bi-mortarboard fs-1 text-info mb-3 d-block"></i>
                  <h3 className="h5 mb-2">{t("teachersPage.more.students.title")}</h3>
                  <p className="text-muted mb-3">{t("teachersPage.more.students.body")}</p>
                  <Link to="/bookmarks-for-students" className="btn btn-sm btn-outline-primary">
                    {t("teachersPage.more.students.link")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm text-center">
                <div className="card-body p-4">
                  <i className="bi bi-journal-text fs-1 text-success mb-3 d-block"></i>
                  <h3 className="h5 mb-2">{t("teachersPage.more.researchers.title")}</h3>
                  <p className="text-muted mb-3">{t("teachersPage.more.researchers.body")}</p>
                  <Link to="/bookmarks-for-researchers" className="btn btn-sm btn-outline-primary">
                    {t("teachersPage.more.researchers.link")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h2 fw-bold mb-4">{t("teachersPage.faq.title")}</h2>
          </div>

          <div className="row g-4">
            {faqData.map((faq, index) => (
              <div key={index} className="col-lg-6">
                <div className="h-100">
                  <h3 className="h5 fw-bold mb-3">{faq.q}</h3>
                  <p className="text-muted">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-5 banner-gradient text-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="h1 fw-bold mb-4 text-white d-flex align-items-center justify-content-center gap-2">
                <LogoOnly width="32" />
                {t("teachersPage.finalCta.title")}
              </h2>
              <p className="lead mb-4">{t("teachersPage.finalCta.lead")}</p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-light btn-lg px-5"
                >
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  {t("teachersPage.finalCta.primary")}
                </Link>
                <Link to="#how-it-works" className="btn btn-outline-light btn-lg px-4">
                  <i className="bi bi-map me-2"></i>
                  {t("teachersPage.finalCta.secondary")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BookmarksForTeachers;
